import React from 'react';

const Base = ({ className = '' }) => (
  <div className={`animate-pulse bg-pw-surface rounded-lg ${className}`} />
);

/** Full-width line placeholder */
export const SkeletonLine = ({ width = 'w-full', height = 'h-3' }) => (
  <Base className={`${width} ${height}`} />
);

/** Circular placeholder */
export const SkeletonCircle = ({ size = 'w-10 h-10' }) => (
  <Base className={`${size} !rounded-full`} />
);

/** Stat card skeleton — matches StatCard layout */
export const StatCardSkeleton = () => (
  <div className="card p-4 space-y-2">
    <div className="flex items-center gap-2">
      <Base className="w-8 h-8 !rounded-xl" />
      <Base className="w-16 h-3" />
    </div>
    <Base className="w-20 h-6" />
    <Base className="w-12 h-2" />
  </div>
);

/** Dashboard skeleton — 4 stat cards + 2 chart placeholders */
export const DashboardSkeleton = () => (
  <div className="space-y-4 animate-fade-in">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {[1, 2, 3, 4].map(i => <StatCardSkeleton key={i} />)}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="card p-5">
        <Base className="w-40 h-4 mb-4" />
        <Base className="w-full h-[260px] !rounded-xl" />
      </div>
      <div className="card p-5">
        <Base className="w-40 h-4 mb-4" />
        <Base className="w-full h-[260px] !rounded-xl" />
      </div>
    </div>
  </div>
);

/** Single row in a list */
const ListRow = () => (
  <div className="p-4 flex items-center gap-3">
    <Base className="w-9 h-9 !rounded-xl flex-shrink-0" />
    <div className="flex-1 space-y-1.5">
      <Base className="w-1/3 h-3.5" />
      <Base className="w-1/2 h-2.5" />
    </div>
    <Base className="w-16 h-5 !rounded-full" />
  </div>
);

/** List skeleton — stacked rows */
export const ListSkeleton = ({ rows = 5 }) => (
  <div className="card overflow-hidden divide-y divide-pw-border-variant/50 animate-fade-in">
    {Array.from({ length: rows }, (_, i) => <ListRow key={i} />)}
  </div>
);

/** Table skeleton — header + rows */
export const TableSkeleton = ({ cols = 4, rows = 6 }) => (
  <div className="card overflow-hidden animate-fade-in">
    <div className="bg-pw-surface px-4 py-3 flex gap-4">
      {Array.from({ length: cols }, (_, i) => (
        <Base key={i} className="h-3 flex-1" />
      ))}
    </div>
    <div className="divide-y divide-pw-border-variant/50">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="px-4 py-3 flex gap-4">
          {Array.from({ length: cols }, (_, j) => (
            <Base key={j} className={`h-3 flex-1 ${j === 0 ? 'max-w-[120px]' : ''}`} />
          ))}
        </div>
      ))}
    </div>
  </div>
);

/** Card grid skeleton — matches badge/resource card layouts */
export const CardGridSkeleton = ({ count = 6, cols = 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' }) => (
  <div className={`grid ${cols} gap-3 animate-fade-in`}>
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="card p-4">
        <div className="flex items-start gap-3">
          <Base className="w-10 h-10 !rounded-xl flex-shrink-0" />
          <div className="flex-1 space-y-1.5">
            <Base className="w-2/3 h-3.5" />
            <Base className="w-full h-2.5" />
            <Base className="w-1/4 h-2" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

/** Header bar skeleton — matches the card p-4 header pattern */
export const HeaderSkeleton = () => (
  <div className="card p-4 flex items-center justify-between animate-fade-in">
    <div className="flex items-center gap-2">
      <Base className="w-4 h-4 !rounded" />
      <Base className="w-32 h-4" />
    </div>
    <Base className="w-24 h-9 !rounded-xl" />
  </div>
);
